import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import InputWithLabel from "../../components/form/input/InputWithLabel";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";

interface LuggageFormData {
  description: string;
  status: boolean;
}

export default function LuggageForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<LuggageFormData>({
    description: "",
    status: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEditMode) {
      fetchLuggageData();
    }
  }, [id]);

  const fetchLuggageData = async () => {
    try {
      const response = await axiosInstance.get(`/luggages/${id}`);
      setFormData(response.data);
    } catch (error) {
      setError("Failed to fetch luggage data");
      console.error("Error fetching luggage:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const submitData = formData;

      if (isEditMode) {
        await axiosInstance.put(`/luggages/${id}`, submitData);
      } else {
        await axiosInstance.post("/luggages", submitData);
      }
      navigate("/luggage");
    } catch (error: any) {
      setError(error.response?.data?.message || "Failed to save luggage");
      console.error("Error saving luggage:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="w-full space-y-6">
      <PageBreadcrumb
        pageTitle={isEditMode ? "Edit Luggage" : "Add Luggage"}
        description={isEditMode ? "Update luggage type details" : "Create a new luggage category"}
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        {error && (
          <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <InputWithLabel
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              placeholder="Enter description"
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                value={formData.status.toString()}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/luggage")}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 